/**
 * HTML overlay — email entry, leaderboard, game-over prompt.
 *
 * Rendered OUTSIDE the Phaser canvas (in #overlay-root from index.html). The
 * overlay lives on top of the canvas via CSS positioning and uses a
 * monochrome white-on-black palette with a monospace font to match the
 * Space Impact aesthetic.
 *
 * Two-step flow:
 *   1. On first visit: email-entry panel. The email is stored in
 *      localStorage so subsequent visits skip the prompt. The display name
 *      is auto-filled from Supabase for returning players.
 *   2. On game over: submit the score to Supabase and show the leaderboard.
 *
 * The functions here are invoked from GameScene (game over → submit) and
 * from the in-canvas button hotkeys (R = restart, L = leaderboard).
 */
import {
  fetchTopScores,
  fetchDisplayNameForEmail,
  isApiConfigured,
  type ScoreRow,
} from "../lib/api";
import { Settings } from "../settings";

const STORAGE_EMAIL = "traszka:email";
const STORAGE_NAME = "traszka:name";

/** Clear stored email and callsign so the next player must enter their own details. */
export function clearPlayerIdent(): void {
  localStorage.removeItem(STORAGE_EMAIL);
  localStorage.removeItem(STORAGE_NAME);
}

/**
 * Pre-fill the email and display-name inputs from localStorage + Supabase.
 * Called once at boot, and again whenever the email-prompt screen opens,
 * so returning players see their previous name already in the input.
 */
export async function prefillPlayerIdent(root: HTMLElement | null): Promise<void> {
  if (!root) return;
  const emailInput = root.querySelector<HTMLInputElement>("#email-input");
  const nameInput = root.querySelector<HTMLInputElement>("#name-input");
  if (!emailInput || !nameInput) return;
  const email = localStorage.getItem(STORAGE_EMAIL) ?? "";
  emailInput.value = email;
  // Try to look up the saved display name locally first; if absent, query
  // Supabase so returning players see the name they used last time.
  const cached = localStorage.getItem(STORAGE_NAME);
  if (cached) {
    nameInput.value = cached;
    return;
  }
  if (!email) {
    nameInput.value = "";
    return;
  }
  const fromServer = await fetchDisplayNameForEmail(email);
  if (fromServer) {
    nameInput.value = fromServer;
  }
}

export function mountOverlay(root: HTMLElement | null): void {
  if (!root) return;
  root.innerHTML = `
    <div id="overlay" data-state="hidden">
      <div class="overlay-panel" data-screen="pause">
        <h2>GAME PAUSED</h2>
        <div class="checkbox-container" style="justify-content: center; gap: 12px; margin-bottom: 12px; margin-top: 0;">
          <label style="cursor: pointer;"><input type="checkbox" id="pause-sfx" ${Settings.sfx ? "checked" : ""} /> SFX</label>
          <label style="cursor: pointer;"><input type="checkbox" id="pause-bgm" ${Settings.bgm ? "checked" : ""} /> MUSIC</label>
        </div>
        <button id="pause-leaderboard">TRASHBOARD</button>
        <button id="pause-continue">CONTINUE</button>
        <button id="pause-restart">RESTART GAME</button>
      </div>
      <div class="overlay-panel" data-screen="leaderboard">
        <h2>TRASHBOARD</h2>
        <p class="contest-status-banner">🏆 CONTEST FINISHED 🏆<br/><span style="font-size: 0.75rem; color: #ffcc00; font-weight: normal;">TOP 3 PLAYERS WINNERS</span></p>
        <ol id="leaderboard-list"></ol>
        <p style="margin: 8px 0 0 0;"><a href="/trashboard" style="color: #ffffff; text-decoration: underline; font-size: 11px;">FULL TOP 100 LIST (/TRASHBOARD)</a></p>
        <button id="leaderboard-close">BACK</button>
      </div>
      <div class="overlay-panel" data-screen="gameover">
        <h2>GAME OVER</h2>
        <p id="gameover-summary">SCORE 0</p>
        <button id="gameover-leaderboard">TRASHBOARD</button>
        <button id="gameover-restart">PLAY AGAIN</button>
      </div>
      <div class="overlay-panel" data-screen="congrats">
        <h2>🏆 MISSION ACCOMPLISHED 🏆</h2>
        <p style="color: #ffff00; font-size: 13px; margin-bottom: 12px; line-height: 1.4;">ALL TRASH RECYCLED!</p>
        <p id="congrats-summary">FINAL SCORE 0</p>
        <p style="font-size: 11px; color: #00ff00; margin-bottom: 14px;">🏆 GAME COMPLETED (+50,000 BONUS)</p>
        <button id="congrats-leaderboard">TRASHBOARD</button>
        <button id="congrats-restart">PLAY AGAIN</button>
      </div>
    </div>
  `;

  root.querySelector<HTMLButtonElement>("#pause-continue")?.addEventListener("click", () => {
    hideOverlay(root);
    window.dispatchEvent(new CustomEvent("traszka:resume"));
  });

  root.querySelector<HTMLButtonElement>("#pause-restart")?.addEventListener("click", () => {
    hideOverlay(root);
    window.dispatchEvent(new CustomEvent("traszka:restart"));
  });

  root.querySelector<HTMLButtonElement>("#pause-leaderboard")?.addEventListener("click", () => {
    void showLeaderboard(root);
  });

  root.querySelector<HTMLButtonElement>("#leaderboard-close")?.addEventListener("click", () => {
    const overlay = root.querySelector<HTMLElement>("#overlay");
    const prev = overlay?.getAttribute("data-prev-state");
    if (prev) {
      overlay?.setAttribute("data-state", prev);
    } else {
      hideOverlay(root);
      window.dispatchEvent(new CustomEvent("traszka:resume"));
    }
  });

  root.querySelector<HTMLButtonElement>("#gameover-leaderboard")?.addEventListener("click", () => {
    void showLeaderboard(root);
  });

  root.querySelector<HTMLButtonElement>("#gameover-restart")?.addEventListener("click", () => {
    hideOverlay(root);
    window.dispatchEvent(new CustomEvent("traszka:restart"));
  });

  root.querySelector<HTMLButtonElement>("#congrats-leaderboard")?.addEventListener("click", () => {
    void showLeaderboard(root);
  });

  root.querySelector<HTMLButtonElement>("#congrats-restart")?.addEventListener("click", () => {
    hideOverlay(root);
    window.dispatchEvent(new CustomEvent("traszka:restart"));
  });

  const pauseSfx = root.querySelector<HTMLInputElement>("#pause-sfx");
  pauseSfx?.addEventListener("change", (e) => {
    Settings.sfx = (e.target as HTMLInputElement).checked;
  });

  const pauseBgm = root.querySelector<HTMLInputElement>("#pause-bgm");
  pauseBgm?.addEventListener("change", (e) => {
    Settings.bgm = (e.target as HTMLInputElement).checked;
    window.dispatchEvent(new CustomEvent("traszka:settings-changed"));
  });

  // Stop keyboard events from propagating to Phaser when user is typing in HTML inputs
  const stopKeyProp = (e: KeyboardEvent): void => {
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) {
      e.stopPropagation();
    }
  };

  window.addEventListener("keydown", stopKeyProp, true);
  window.addEventListener("keyup", stopKeyProp, true);
  window.addEventListener("keypress", stopKeyProp, true);
}

export function ensurePlayerIdent(_root: HTMLElement | null): boolean {
  // Contest finished: welcome & email prompts are disabled.
  return true;
}

export async function showInfoPrompt(_root: HTMLElement | null): Promise<void> {
  // Welcome window disabled — contest is finished.
}

export async function showEmailPrompt(_root: HTMLElement | null): Promise<void> {
  // Email prompt disabled — contest is finished.
}

export async function showPauseMenu(root: HTMLElement | null): Promise<void> {
  if (!root) return;
  const overlay = root.querySelector<HTMLElement>("#overlay");
  if (!overlay) return;
  // Sync checkbox state in case it was changed in StartScene
  const pauseSfx = root.querySelector<HTMLInputElement>("#pause-sfx");
  if (pauseSfx) pauseSfx.checked = Settings.sfx;
  const pauseBgm = root.querySelector<HTMLInputElement>("#pause-bgm");
  if (pauseBgm) pauseBgm.checked = Settings.bgm;
  
  overlay.setAttribute("data-state", "pause");
}

export async function showLeaderboard(root: HTMLElement | null): Promise<void> {
  if (!root) return;
  const overlay = root.querySelector<HTMLElement>("#overlay");
  const list = root.querySelector<HTMLOListElement>("#leaderboard-list");
  if (!overlay || !list) return;
  
  const currentState = overlay.getAttribute("data-state");
  if (currentState && currentState !== "leaderboard" && currentState !== "hidden") {
    overlay.setAttribute("data-prev-state", currentState);
  }
  
  overlay.setAttribute("data-state", "leaderboard");
  if (!(await isApiConfigured())) {
    list.innerHTML =
      "<li>TRASHBOARD OFFLINE — server didn't start (check Railway logs).</li>";
    return;
  }
  list.innerHTML = "<li>LOADING…</li>";
  
  try {
    const rows: ScoreRow[] = await fetchTopScores(20);
    if (rows.length === 0) {
      list.innerHTML = "<li>NO SCORES YET. BE THE FIRST.</li>";
    } else {
      list.innerHTML = rows
        .map(
          (r, i) => {
            let rankText = `${(i + 1).toString().padStart(2, "0")}.`;
            let itemClass = "";
            if (i === 0) {
              rankText = "🥇 01.";
              itemClass = "class='top-winner rank-1'";
            } else if (i === 1) {
              rankText = "🥈 02.";
              itemClass = "class='top-winner rank-2'";
            } else if (i === 2) {
              rankText = "🥉 03.";
              itemClass = "class='top-winner rank-3'";
            }
            return `<li ${itemClass}><span>${rankText}</span> ${escapeHtml(r.display_name)}${r.completed ? " 🏆" : ""} — <strong>${r.score}</strong></li>`;
          }
        )
        .join("");
    }
  } catch (err) {
    list.innerHTML = `<li>TRASHBOARD UNREACHABLE: ${escapeHtml((err as Error).message)}</li>`;
  }
}

export function showGameOver(root: HTMLElement | null, score: number, level: number, cycle: number): void {
  if (!root) return;
  const overlay = root.querySelector<HTMLElement>("#overlay");
  const summary = root.querySelector<HTMLElement>("#gameover-summary");
  if (!overlay) return;
  if (summary) {
    summary.textContent = `SCORE ${score} — LEVEL ${level}${cycle > 0 ? ` — CYCLE ${cycle + 1}` : ""}`;
  }
  overlay.removeAttribute("data-prev-state");
  overlay.setAttribute("data-state", "gameover");
}

export function showCongratsPanel(root: HTMLElement | null, score: number, wave: number): void {
  if (!root) return;
  const overlay = root.querySelector<HTMLElement>("#overlay");
  const summary = root.querySelector<HTMLElement>("#congrats-summary");
  if (!overlay) return;
  if (summary) {
    summary.textContent = `FINAL SCORE ${score} — WAVE ${wave}/15`;
  }
  overlay.removeAttribute("data-prev-state");
  overlay.setAttribute("data-state", "congrats");
}

/** Submit a finished run's score to the local API. Score submissions frozen. */
export async function submitRunScore(_score: number, _level: number, _completed = false): Promise<void> {
  // Highscore submission frozen — contest is finished.
  return Promise.resolve();
}

function hideOverlay(root: HTMLElement): void {
  root.querySelector<HTMLElement>("#overlay")?.setAttribute("data-state", "hidden");
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}